/**
 * Location Performance — the filter drawer.
 *
 * Opens on arrival and holds the report closed until Start Date and End Date
 * are set: the query groups over every conversation in the window, so running
 * it unbounded would be a slow query for a result nobody asked for.
 *
 * Group By is the report's spine rather than an extra filter — it decides what
 * a row IS. It defaults to Location (the report's name) and offers every
 * conversation property the API whitelists, served by the same endpoint so the
 * list and the whitelist cannot drift.
 *
 * Location → Branch → Department cascade: choosing a location narrows the
 * branches, and a branch narrows the departments. Selections that fall out of
 * scope are dropped rather than silently filtering on something invisible.
 */
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { Calendar } from 'primereact/calendar'
import { RotateCcw, SlidersHorizontal } from 'lucide-react'

import ResourceDrawer from '../../admin/ResourceDrawer.js'
import { useCascade, useReportOrgFilters } from '../../../hooks/useReportOrgFilters.js'
import type { LocationPerformanceFilters as Filters } from '../../../hooks/useLocationPerformanceReport.js'
import { useOrgTimezone } from '../../../hooks/useOrgTimezone.js'

interface Props {
  open: boolean
  onClose: () => void
  onRun: (filters: Filters) => void
  onReset: () => void
  running: boolean
}

/** `YYYY-MM-DD` in LOCAL time — `toISOString()` would shift the day westward. */
function toDateString(d: Date | null): string {
  if (!d) return ''
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default function LocationPerformanceFilters({
  open,
  onClose,
  onRun,
  onReset,
  running,
}: Props): JSX.Element {
  const browserZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  )
  const { timezone: orgTimezone } = useOrgTimezone()

  // Start on the resolved timezone if it's already cached (no flash), or the
  // browser zone as a placeholder while the first fetch is in flight.
  const [timezone, setTimezone] = useState(orgTimezone || browserZone)

  // Once the resolved timezone arrives, adopt it — but only if the user hasn't
  // manually changed the dropdown yet (timezone still matches the initial value).
  useEffect(() => {
    if (orgTimezone) setTimezone(orgTimezone)
  }, [orgTimezone])
  const [groupBy, setGroupBy] = useState('location')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [locationIds, setLocationIds] = useState<string[]>([])
  const [branchIds, setBranchIds] = useState<string[]>([])
  const [departmentIds, setDepartmentIds] = useState<string[]>([])
  const [touched, setTouched] = useState(false)

  const { options, loading } = useReportOrgFilters()
  const { branches, departments } = useCascade(options, locationIds, branchIds)

  const timezoneOptions = useMemo(() => {
    const list = options.timezones.map((t) => ({
      label: t.offset ? `${t.label} (${t.offset})` : t.label,
      value: t.value,
    }))
    // Keep the browser zone selectable even if the seeded table lacks it —
    // otherwise the default would silently fall back to something else.
    if (!list.some((t) => t.value === browserZone)) {
      list.unshift({ label: browserZone, value: browserZone })
    }
    return list
  }, [options.timezones, browserZone])

  // Drop selections the cascade no longer offers. Joined keys, not the arrays:
  // a new array identity each render would clear the selection constantly.
  const branchKey = branches.map((b) => b.value).join(',')
  const departmentKey = departments.map((d) => d.value).join(',')
  useEffect(() => {
    const allowed = new Set(branchKey.split(',').filter(Boolean))
    setBranchIds((prev) => prev.filter((id) => allowed.has(id)))
  }, [branchKey])
  useEffect(() => {
    const allowed = new Set(departmentKey.split(',').filter(Boolean))
    setDepartmentIds((prev) => prev.filter((id) => allowed.has(id)))
  }, [departmentKey])

  const errors = {
    startDate: !startDate ? 'Start date is required' : '',
    endDate: !endDate
      ? 'End date is required'
      : startDate && endDate < startDate
        ? 'End date must be on or after start date'
        : '',
  }
  const valid = !errors.startDate && !errors.endDate

  const handleRun = (): void => {
    setTouched(true)
    if (!valid) return
    onRun({
      timezone,
      groupBy,
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      locationIds,
      branchIds,
      departmentIds,
    })
  }

  const handleReset = (): void => {
    setTimezone(orgTimezone)
    setGroupBy('location')
    setStartDate(null)
    setEndDate(null)
    setLocationIds([])
    setBranchIds([])
    setDepartmentIds([])
    setTouched(false)
    onReset()
  }

  const showError = (msg: string): JSX.Element | null =>
    touched && msg ? <small className="mt-1 block text-xs font-medium text-red-600">{msg}</small> : null

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => { handleRun() }}
      saving={running}
      disabled={touched && !valid}
      width="xl"
      saveLabel={running ? 'Running…' : 'Run Report'}
      title="Report Filters"
    >
      <div className="kc3-form">
        <p className="mb-4 flex items-center gap-2 text-xs text-slate-500">
          <SlidersHorizontal className="h-3.5 w-3.5 text-[#3366FF]" aria-hidden="true" />
          Choose a window and what to break the conversations up by, then run the report.
        </p>

        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">Reporting window</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Timezone">
            <Dropdown
              value={timezone}
              options={timezoneOptions}
              onChange={(e) => setTimezone(String(e.value))}
              filter
              placeholder="Select timezone"
              className="w-full"
            />
          </Field>

          <Field label="Group By" required>
            <Dropdown
              value={groupBy}
              options={options.groupByOptions}
              onChange={(e) => setGroupBy(String(e.value))}
              filter
              placeholder={loading ? 'Loading…' : 'Select a property'}
              className="w-full"
            />
            <small className="mt-1 block text-[11px] text-slate-400">
              Each row of the report is one value of this property.
            </small>
          </Field>

          <Field label="Start Date" required>
            <Calendar
              value={startDate}
              onChange={(e) => setStartDate((e.value as Date | null) ?? null)}
              dateFormat="yy-mm-dd"
              showIcon
              placeholder="Select start date"
              className={`w-full ${touched && errors.startDate ? 'p-invalid' : ''}`}
              maxDate={endDate ?? undefined}
            />
            {showError(errors.startDate)}
          </Field>

          <Field label="End Date" required>
            <Calendar
              value={endDate}
              onChange={(e) => setEndDate((e.value as Date | null) ?? null)}
              dateFormat="yy-mm-dd"
              showIcon
              placeholder="Select end date"
              className={`w-full ${touched && errors.endDate ? 'p-invalid' : ''}`}
              minDate={startDate ?? undefined}
            />
            {showError(errors.endDate)}
          </Field>

        </div>

        <p className="mb-3 mt-5 border-t border-slate-100 pt-4 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">Organisation</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Location">
            <MultiSelect
              value={locationIds}
              options={options.locations}
              onChange={(e) => setLocationIds((e.value as string[]) ?? [])}
              filter
              display="chip"
              maxSelectedLabels={2}
              selectedItemsLabel="{0} locations"
              placeholder={loading ? 'Loading…' : 'All locations'}
              className="w-full"
            />
          </Field>

          <Field label="Branch">
            <MultiSelect
              value={branchIds}
              options={branches}
              onChange={(e) => setBranchIds((e.value as string[]) ?? [])}
              filter
              display="chip"
              maxSelectedLabels={2}
              selectedItemsLabel="{0} branches"
              placeholder={branches.length === 0 ? 'No branches in scope' : 'All branches'}
              className="w-full"
            />
          </Field>

          <Field label="Department">
            <MultiSelect
              value={departmentIds}
              options={departments}
              onChange={(e) => setDepartmentIds((e.value as string[]) ?? [])}
              filter
              display="chip"
              maxSelectedLabels={2}
              selectedItemsLabel="{0} departments"
              placeholder={departments.length === 0 ? 'No departments in scope' : 'All departments'}
              className="w-full"
            />
          </Field>
        </div>

        <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-500">
            The window is on conversation creation date.
          </p>
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Reset
          </button>
        </div>
      </div>
    </ResourceDrawer>
  )
}

function Field({
  label,
  required = false,
  children,
}: { label: string; required?: boolean; children: ReactNode }): JSX.Element {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required && <span aria-hidden className="ml-1 text-red-500">*</span>}
      </label>
      {children}
    </div>
  )
}
